import AlertPopup from "components/alert-popup/AlertPopup";
import BillingAccountantInfoForm from "components/form/billing-client/BillingAccountantInfoForm";
import BillingClientPersonalInfoForm from "components/form/billing-client/BillingClientPersonalInfoForm";
import BillingClientCorporateInfoForm from "components/form/billing-client/BillingClientCorporateInfoForm";
import Button from "core/buttons/button/Button";
import InfoContext from "pages/InfoContext";
import ValidationModal from "components/modal/ValidationModal";
import { BILLING_COMPANY_FORM_DEFAULT_VALUES } from "constants/forms";
import { FIELD_REQUIRED } from "constants/popup-codes";
import { FormattedMessage } from "react-intl";
import { MODULES, QUERY_STATUS, POPUP_STATUS } from "constants/constants";
import { useBillingClientContext } from "api/directory/billing-client/BillingClientProvider";
import { useCountryContext } from "api/settings/country/CountryProvider";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import { useNavigate, useLocation } from "react-router-dom";
import { useSettingsContext } from "api/settings/SettingsProvider";
import { useUserContext } from "api/user/UserProvider";
import { useApiFieldErrors } from "utils/useApiFieldErrors";
import colors from "assets/styles/mixins/colors.scss";

const CreateBillingClient = () => {
  const location = useLocation();
  const { clientId, clientSlug } = location.state || {};
  const { lang, userScope } = useUserContext();
  const {
    dataCountry,
    getDataCountry,
    dataTvaRegime,
    getDataTvaRegime,
    dataPaymentCondition,
    getDataPaymentCondition,
  } = useSettingsContext();
  const { countryList, countryListGetter } = useCountryContext();
  const navigate = useNavigate();
  const {
    searchDavidsonBySiren,
    sirenData,
    setSirenData,
    sirenErrorMessage,
    setSirenErrorMessage,
  } = useGeneralContext();
  const {
    createNewBillingClient,
    createdBillingClientStatus,
    createdBillingClient,
  } = useBillingClientContext();
  const {
    control,
    formState: { errors },
    register,
    setValue,
    watch,
    clearErrors,
    handleSubmit,
    reset,
    setError,
  } = useForm({
    defaultValues: BILLING_COMPANY_FORM_DEFAULT_VALUES,
  });
  const [code, setCode] = useState();
  const [status, setStatus] = useState();
  const [isOpen, setIsOpen] = useState(false);
  const [cellHighlight, setCellHighlight] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  // Synchronous lock: blocks re-entrant submits before React re-renders the disabled button
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    isValidated && errors && setCellHighlight(true);
  }, [errors, isValidated]);

  useApiFieldErrors(setError);

  const onSubmit = async (info) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsOpen(true);
    try {
      await createNewBillingClient({
        ...info,
        nationality_id: info?.nationality_id[0]?.value,
        payment_condition_id: info?.payment_condition_id,
        billing_country:
          info?.billing_country[0]?.value || info?.billing_country,
        assign_client: clientId || null,
      });
    } catch {
      // Provider already set createdBillingClientStatus to ERROR and apiErrorResponse
    } finally {
      isSubmittingRef.current = false;
    }
  };

  useEffect(() => {
    countryListGetter();
    getDataCountry();
    getDataTvaRegime();
    getDataPaymentCondition();
    setSirenData({});
  }, [
    getDataCountry,
    getDataTvaRegime,
    getDataPaymentCondition,
    countryListGetter,
    setSirenData,
  ]);

  useEffect(() => {
    if (
      createdBillingClientStatus === QUERY_STATUS.SUCCESS &&
      createdBillingClient?.id
    ) {
      setSirenData({});
    }
  }, [createdBillingClientStatus, createdBillingClient]);

  const resetForm = () => {
    reset(BILLING_COMPANY_FORM_DEFAULT_VALUES);
    setSirenData({});
    window.scrollTo(0, 0);
    setIsValidated(false);
  };

  const onError = (formErrors) => {
    setStatus(POPUP_STATUS.ERROR);
    const sirenError = formErrors?.siren?.message;
    if (sirenError && typeof sirenError === "string") {
      setCode(sirenError);
    } else {
      setCode(FIELD_REQUIRED);
    }
  };

  const value = useMemo(
    () => ({
      countryList,
      dataCountry,
      dataPaymentCondition,
      dataTvaRegime,
      lang,
      module: MODULES.BILLING_CLIENT,
      navigate,
      sirenErrorMessage,
      queryData: createdBillingClient,
      queryStatus: createdBillingClientStatus,
      searchDavidsonBySiren,
      setSirenErrorMessage,
      setSirenData,
      sirenData,
      userScope,
      setCellHighlight,
      setStatus,
      setCode,
      code,
    }),
    [
      countryList,
      setCellHighlight,
      createdBillingClientStatus,
      createdBillingClient,
      dataCountry,
      dataPaymentCondition,
      dataTvaRegime,
      lang,
      sirenErrorMessage,
      sirenData,
      userScope,
      navigate,
      searchDavidsonBySiren,
      setSirenErrorMessage,
      setSirenData,
      setStatus,
      setCode,
      code,
    ]
  );
  return (
    <>
      <ValidationModal
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
          if (
            createdBillingClientStatus === QUERY_STATUS.SUCCESS &&
            createdBillingClient?.slug
          ) {
            const path = clientSlug
              ? `/${userScope}/client/${clientSlug}`
              : `/${userScope}/billing-client/${createdBillingClient.slug}`;
            navigate(path);
          }
        }}
        status={createdBillingClientStatus}
        messages={{
          pendingTitleId: "modal_validation_create_file",
          pendingMessageId: "modal_validation_file_pending_msg",
          successTitleId: "modal_validation_create_file",
          successMessageId: "modal_validation_file_success_msg",
          errorTitleId: "modal_validation_create_file",
          errorMessageId: "modal_validation_field_errors_msg",
          errorMessageDefault:
            "Certains champs comportent des erreurs.{br}Veuillez cliquer sur les <nowrap>icônes{errorIcon}pour</nowrap> obtenir plus de détails et corriger les informations nécessaires.",
        }}
      />
      <InfoContext.Provider value={value}>
        <AlertPopup
          code={code}
          setCode={setCode}
          setStatus={setStatus}
          status={status}
        />
        <form
          onSubmit={handleSubmit(onSubmit, onError)}
          style={{ paddingTop: "2rem" }}
        >
          <BillingClientPersonalInfoForm
            maxLength={128}
            watch={watch}
            setError={setError}
            clearErrors={clearErrors}
            control={control}
            errors={errors}
            register={register}
            setValue={(field, value) => {
              setValue(field, value);
              clearErrors(field);
            }}
            cellHighlight={cellHighlight}
          />
          <div className="bottom-billing-client-wrapper ">
            <BillingClientCorporateInfoForm
              watch={watch}
              setError={setError}
              clearErrors={clearErrors}
              control={control}
              errors={errors}
              register={register}
              setValue={(field, value) => {
                setValue(field, value);
                clearErrors(field);
              }}
              cellHighlight={cellHighlight}
            />
            <BillingAccountantInfoForm
              watch={watch}
              setError={setError}
              clearErrors={clearErrors}
              control={control}
              errors={errors}
              register={register}
              setValue={(field, value) => {
                setValue(field, value);
                clearErrors(field);
              }}
              cellHighlight={cellHighlight}
            />
          </div>
          <div className="form-buttons">
            <Button
              className="btn-standard btn-outline"
              onClick={resetForm}
              type="button"
              id="button-form-reset"
            >
              <FormattedMessage id="cancel" defaultMessage="Annuler" />
            </Button>
            <Button
              color="btn-medium gray-700"
              type="submit"
              id="button-form-validation"
              onClick={() => setIsValidated(true)}
              disabled={createdBillingClientStatus === QUERY_STATUS.LOADING}
              backgroundColor={
                createdBillingClientStatus === QUERY_STATUS.LOADING &&
                colors.disabledGrey
              }
            >
              <FormattedMessage
                id="button_form_validation"
                defaultMessage="Valider la création"
              />
            </Button>
          </div>
        </form>
      </InfoContext.Provider>
    </>
  );
};

export default CreateBillingClient;
